import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Skeleton,
  Stack,
} from '@hurdl/design-system';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useState, type ReactElement } from 'react';

import {
  ItemEditorFrame,
  QueueEditor,
} from '../bespoke/menu-workspace/review-editor.js';
import {
  type Category,
  type ReviewItem,
} from '../bespoke/menu-workspace/types.js';
import {
  ALLERGEN_BOOKLET_URL,
  FAST_FOOD_RESTAURANT_KEY,
  restaurantLabelOf,
  useMenuOperations,
} from './menus-wiring.js';
import { useToast } from './toast-context.js';

/**
 * The focused review of one queued item (menus-spec R13, R15, R16).
 *
 * Reads the item key from the route, loads the queue and the Restaurant's managed
 * Categories, and renders the reused {@link QueueEditor} for the one item — a new item to
 * categorise, or a significant allergen change to confirm or reject — beside the source
 * PDF in an on-page Dialog. A submission is sequenced into the backend writes by the
 * composed operations; on success a toast reports it and the page advances to the next
 * queued item, or back to the queue when none is left. A failure is reported in a
 * destructive toast and the Operator stays on the item.
 *
 * An item key that is not in the queue — already reviewed, or never queued — shows a
 * message and a link back to the queue rather than a blank editor. The one `h1` is the
 * page's; the `main` landmark is the Layout's.
 */
export function ReviewItemPage(): ReactElement {
  const { productKey, itemKey } = useParams({ strict: false });
  const operations = useMenuOperations();
  const navigate = useNavigate();
  const { notify } = useToast();

  const [queue, setQueue] = useState<ReadonlyArray<ReviewItem> | undefined>(
    undefined,
  );
  const [categories, setCategories] = useState<ReadonlyArray<Category>>([]);

  useEffect(() => {
    let live = true;
    void Promise.all([
      operations.listQueue(),
      operations.listCategories(FAST_FOOD_RESTAURANT_KEY),
    ]).then(([nextQueue, nextCategories]) => {
      if (!live) return;
      setQueue(nextQueue);
      setCategories(nextCategories);
    });
    return () => {
      live = false;
    };
  }, [operations]);

  if (queue === undefined) {
    return (
      <Stack gap="large" aria-busy="true">
        <h1 className="font-hurdl-display text-hurdl-heading font-hurdl-semibold">
          Review item
        </h1>
        <Skeleton className="h-96 w-full" />
      </Stack>
    );
  }

  const item = queue.find((candidate) => candidate.itemKey === itemKey);

  if (item === undefined) {
    return (
      <Stack gap="large">
        <h1 className="font-hurdl-display text-hurdl-heading font-hurdl-semibold">
          Review item
        </h1>
        <Alert tone="informational">
          <AlertTitle>Not in the queue</AlertTitle>
          <AlertDescription>
            This item is no longer awaiting review.{' '}
            <Link
              to="/products/$productKey/review"
              params={{ productKey: productKey ?? '' }}
              className="underline underline-offset-2"
            >
              Back to the review queue
            </Link>
            .
          </AlertDescription>
        </Alert>
      </Stack>
    );
  }

  const submit = (
    submission: Parameters<typeof operations.submitReviewedItem>[1],
  ): void => {
    void operations
      .submitReviewedItem(item.itemKey, submission)
      .then(() => {
        notify('success', `Reviewed ${item.extracted.name}`);
        const next = queue.find(
          (candidate) => candidate.itemKey !== item.itemKey,
        );
        if (next === undefined) {
          void navigate({
            to: '/products/$productKey/review',
            params: { productKey: productKey ?? '' },
          });
        } else {
          void navigate({
            to: '/products/$productKey/review/$itemKey',
            params: { productKey: productKey ?? '', itemKey: next.itemKey },
          });
        }
      })
      .catch((error: unknown) => {
        notify('destructive', 'Review not saved', messageOf(error));
      });
  };

  return (
    <ItemEditorFrame
      title={item.extracted.name}
      bookletUrl={ALLERGEN_BOOKLET_URL}
      badges={
        <>
          <Badge tone="neutral">{restaurantLabelOf(item.restaurantKey)}</Badge>
          <Badge
            tone={item.reason === 'new-item' ? 'informational' : 'warning'}
          >
            {item.reason === 'new-item' ? 'New item' : 'Allergen change'}
          </Badge>
        </>
      }
    >
      <QueueEditor
        key={item.itemKey}
        item={item}
        categories={categories}
        onSubmit={submit}
        onCancel={() => {
          void navigate({
            to: '/products/$productKey/review',
            params: { productKey: productKey ?? '' },
          });
        }}
      />
    </ItemEditorFrame>
  );
}

/** The message of a thrown refusal, or a fallback where it carries none. */
function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : 'Please try again.';
}
